import React, { useState, useEffect } from 'react';
import Footer from '../../components/Footer';
import LandingHeader from '../components/LandingHeader';
import { AppLayout } from '../components/AppLayout';
import { QuickStartCard } from '../components/KnowledgeBase/QuickStartCard';
import { VideoSection } from '../components/KnowledgeBase/VideoSection';
import { VideoModalKB } from '../components/KnowledgeBase/VideoModalKB';
import { ExtraResources } from '../components/KnowledgeBase/ExtraResources';
import { isIOSDevice } from '../utils/deviceDetection';
import { useAuth } from '../components/AuthProviderWrapper';
import { BackButton } from '../components/BackButton';
import { getVideos } from '../services/videoService';
import type { Video } from '../types/video';

const KnowledgeBasePage: React.FC = () => {
  const isIOS = isIOSDevice();
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const videoList = await getVideos({ limit: 6, sort_by: 'newest' });
      setVideos(videoList);
    } catch (_error) {
      // Error loading videos - silently fail
    } finally {
      setLoadingVideos(false);
    }
  };

  
  const quickStartCards = [
    {
      to: '/knowledge-base/recipes/how-to-add-recipe',
      borderColor: '#E07A55',
      iconColor: '#E07A55',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>,
      title: 'Add Your First Recipe',
      description: 'Import from web, paste text, or enter manually'
    },
    {
      to: '/knowledge-base/meal-planning/add-to-meal-plan',
      borderColor: '#D98E73',
      iconColor: '#D98E73',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>,
      title: 'Create a Meal Plan',
      description: 'Plan your week in minutes'
    },
    {
      to: '/knowledge-base/shopping-list/shopping-list-features',
      borderColor: '#6E9C87',
      iconColor: '#6E9C87',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>,
      title: 'Build Shopping Lists',
      description: 'Never forget an ingredient again'
    },
    {
      to: '/knowledge-base/recipes/cooking-mode',
      borderColor: '#3C6E71',
      iconColor: '#3C6E71',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/></svg>,
      title: 'Cooking Mode Features',
      description: 'Interactive step-by-step cooking guide'
    }
  ];

  const content = (
    <div 
      className="flex-grow container mx-auto px-4 py-8 max-w-6xl"
      style={{ 
        marginTop: isAuthenticated ? (isIOS ? '4rem' : '0') : (isIOS ? 'calc(4rem + env(safe-area-inset-top, 0px))' : '4rem')
      }}
    >
        {/* Back Button */}
        <div className="mb-4">
          <BackButton />
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#4A6651' }}>
            DinnerBrain Knowledge Base 
          </h1>
          <p className="text-lg" style={{ color: '#598D76' }}>
            Welcome to your complete guide to mastering DinnerBrain! Whether you&apos;re a meal planning newbie or a macro-tracking master chef, we&apos;ve got all the answers.
          </p>
        </div>

        {/* Quick Start Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {quickStartCards.map(card => (
            <QuickStartCard key={card.to} {...card} />
          ))}
        </div>

        {/* Video Tutorials Section */}
        <VideoSection
          videos={videos}
          loadingVideos={loadingVideos}
          onVideoSelect={setSelectedVideo}
        />

        {/* Video Modal */}
        {selectedVideo && (
          <VideoModalKB
            video={selectedVideo}
            onClose={() => setSelectedVideo(null)}
          />
        )}

        <ExtraResources />
    </div>
  );

  // If logged in, use AppLayout with regular header
  if (isAuthenticated) {
    return (
      <AppLayout>
        <div style={{ backgroundColor: '#E6EFEA', minHeight: 'calc(100vh - 200px)' }}>
          {content}
        </div>
      </AppLayout>
    );
  }

  // If logged out, use LandingHeader
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#E6EFEA' }}>
      <LandingHeader />
      {content}
      <Footer />
    </div>
  );
};

export default KnowledgeBasePage;